import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  FieldLabel,
  KeyValueCustomSelect,
  NumberInputField,
  Paragraph,
  Text,
  Title,
  useSelectState,
  whiteButtonStyles,
} from "@smartsuite/react-ui/lib";

import {
  CanvasPaperConfig,
  PaddingDirections,
} from "../PageDesigner/PageDesigner";
import {
  DirectionsInputs,
  updateValues,
} from "../SidebarSelectionConfig/DirectionsInputs/DirectionsInputs";
import {
  BackgroundStyle,
  backgroundStyleOptions,
  PaperOrientation,
  PaperOrientationOptions,
  PaperSize,
  paperSizeConfigs,
  paperSizeOptions,
} from "../paper.constants";
import { showElementWithoutImplementation } from "../utils";
import { StyledSwitchButton } from "../StyledSwitchButton/StyledSwitchButton";

export interface SidebarPaperConfigProps {
  canvasPaperConfig: CanvasPaperConfig;
  onCanvasPaperConfigUpdate: (
    onCanvasPaperConfigUpdate: CanvasPaperConfig,
  ) => void;
}

export const SidebarPaperConfig: React.FC<SidebarPaperConfigProps> = ({
  canvasPaperConfig,
  onCanvasPaperConfigUpdate,
}) => {
  const [paperSizeSelectState, setPaperSizeSelectState] = useSelectState();
  const [paperSize, setPaperSize] = useState(PaperSize.Letter);
  const [orientation, setOrientation] = useState<PaperOrientation>(
    canvasPaperConfig.orientation,
  );
  const [pageBreakMargin, setPageBreakMargin] = useState(
    canvasPaperConfig.pageBreakMargin ?? 0,
  );
  const [backgroundStyle, setBackgroundStyle] = useState(
    BackgroundStyle.FitCanvas,
  );
  const [paddingDirections, setPaddingDirections] = useState<PaddingDirections>(
    {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  );

  const pageBreakMarginRef = useRef<HTMLInputElement>(null);
  const [pageBreakMarginSelectState, setPageBreakMarginSelectState] =
    useSelectState();

  useEffect(() => {
    const { height, width } = canvasPaperConfig;
    const compareHeight = height > width ? height : width;
    const compareWidth = height > width ? width : height;
    const paperSizeConfig = Object.values(paperSizeConfigs).find(
      (paperSizeConfig) =>
        paperSizeConfig.height === compareHeight &&
        paperSizeConfig.width === compareWidth,
    );

    if (paperSizeConfig) {
      setPaperSize(
        Object.keys(paperSizeConfigs).find(
          (key) => paperSizeConfigs[key as PaperSize] === paperSizeConfig,
        ) as PaperSize,
      );
    }
  }, [
    canvasPaperConfig,
    canvasPaperConfig.height,
    canvasPaperConfig.width,
    orientation,
  ]);

  const resizePaper = useCallback(
    (paperSize: PaperSize) => {
      const config: CanvasPaperConfig = {
        ...paperSizeConfigs[paperSize],
        paddingDirections,
        pageBreakMargin: canvasPaperConfig.pageBreakMargin,
      };

      onCanvasPaperConfigUpdate(config);
      setOrientation(config.orientation);
      setPaperSize(paperSize);
    },
    [
      canvasPaperConfig.pageBreakMargin,
      onCanvasPaperConfigUpdate,
      paddingDirections,
    ],
  );

  const updatePaperOrientation = useCallback(
    (value: PaperOrientation) => {
      let newHeight = 0;
      let newWidth = 0;
      const { height, width } = paperSizeConfigs[paperSize];
      if (value === PaperOrientation.Portrait) {
        newHeight = height > width ? height : width;
        newWidth = height > width ? width : height;
      }
      if (value === PaperOrientation.Landscape) {
        newHeight = height > width ? width : height;
        newWidth = height > width ? height : width;
      }
      onCanvasPaperConfigUpdate({
        height: newHeight,
        width: newWidth,
        paddingDirections,
        orientation: value,
        pageBreakMargin,
      });
      setOrientation(value);
    },
    [onCanvasPaperConfigUpdate, paddingDirections, pageBreakMargin, paperSize],
  );

  const updateSpacing = useCallback(
    (value: updateValues) => {
      const { height, width } = paperSizeConfigs[paperSize];
      const directionsUpdate = {
        ...paddingDirections,
        [value.direction]: value.value,
      };
      setPaddingDirections(directionsUpdate);
      onCanvasPaperConfigUpdate({
        height,
        width,
        paddingDirections: directionsUpdate,
        orientation,
        pageBreakMargin,
      });
    },
    [
      onCanvasPaperConfigUpdate,
      orientation,
      paddingDirections,
      pageBreakMargin,
      paperSize,
    ],
  );

  const updatePageBreakMargin = useCallback(
    (value: number) => {
      setPageBreakMargin(value);
      onCanvasPaperConfigUpdate({
        ...canvasPaperConfig,
        pageBreakMargin: value,
      });
    },
    [canvasPaperConfig, onCanvasPaperConfigUpdate],
  );

  return (
    <div className="sidebar-paper-config">
      <div className="sidebar-paper-config__section sidebar-paper-config__section__overflow">
        <Title size="xs" weight="medium">
          Design Settings
        </Title>

        <div>
          <Text
            tag="div"
            weight="medium"
            color={paperSizeSelectState === "active" ? "link" : "primary"}
            className="sidebar-paper-config__title"
          >
            Paper Size
          </Text>

          <KeyValueCustomSelect
            state={paperSizeSelectState}
            value={paperSize}
            options={paperSizeOptions}
            placeholderArrowStyle="filled-dark"
            showClear={false}
            onChange={(paperSizeValue) =>
              resizePaper(paperSizeValue as PaperSize)
            }
            onOpenedChange={setPaperSizeSelectState}
          />
        </div>

        <div>
          <Text
            tag="div"
            weight="medium"
            className="sidebar-paper-config__title"
          >
            Paper Orientation
          </Text>
          <StyledSwitchButton
            options={PaperOrientationOptions}
            currentValue={orientation}
            onChange={(orientationValue) =>
              updatePaperOrientation(orientationValue as PaperOrientation)
            }
          />
        </div>

        <div>
          <header className="sidebar-paper-config__title">
            <Text tag="div" weight="medium">
              Page Break Margin
            </Text>
            <Paragraph
              color="inactive"
              size="xs"
              className="sidebar-paper-config__subtitle"
            >
              Margin applied to elements before and after a page break.
            </Paragraph>
          </header>
          <NumberInputField
            ref={pageBreakMarginRef}
            onFocus={() => setPageBreakMarginSelectState(true)}
            onBlur={() => setPageBreakMarginSelectState(false)}
            state={pageBreakMarginSelectState ? "active" : undefined}
            showSymbol={false}
            autoHideNavigation={true}
            inputStyles={{
              backgroundColor: "white",
              borderColor: "G-1-5",
              borderHoverColor: "G-3",
              borderFocusColor: "G-3",
            }}
            max="999"
            min="0"
            value={`${pageBreakMargin}`}
            onChangeValue={(value) => {
              if (pageBreakMarginRef.current)
                pageBreakMarginRef.current.focus();
              updatePageBreakMargin(value ? parseInt(value) : 0);
            }}
          />
        </div>

        {showElementWithoutImplementation && (
          <>
            <div>
              <FieldLabel>Background Image</FieldLabel>
              <Button
                className="sidebar-paper-config__full sidebar-paper-config__text-center"
                styles={{
                  ...whiteButtonStyles,
                  height: 36,
                }}
              >
                Browse File
              </Button>
            </div>
            <div>
              <FieldLabel>Background Style</FieldLabel>
              <KeyValueCustomSelect
                value={backgroundStyle}
                options={backgroundStyleOptions}
                onChange={(backgroundStyleValue) =>
                  setBackgroundStyle(backgroundStyleValue as BackgroundStyle)
                }
              />
            </div>
          </>
        )}

        {showElementWithoutImplementation && (
          <DirectionsInputs
            title="Padding"
            values={paddingDirections}
            updateValues={(value) => updateSpacing(value)}
          />
        )}
      </div>
    </div>
  );
};
